import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

const API_BASE_URL: string =
  (import.meta as any)?.env?.VITE_API_BASE_URL || 'http://localhost:3001/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

function demoIsActiveInBrowser(): boolean {
  try {
    const status = JSON.parse(localStorage.getItem('taratic:demo:status') || 'null');
    return !!(status?.allowed && status?.active);
  } catch {
    return false;
  }
}

function applyAuthentication(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const isAdminApp = typeof window !== 'undefined' && String(window.location.pathname || '').startsWith('/admin');
  const isAdminUrl = config.url?.includes('/admin/');
  const isAdminRequest = isAdminApp || isAdminUrl;

  const method = String(config.method || 'get').toLowerCase();
  const guardedPath = ['/chat', '/v1/chat', '/clinics', '/v1/instagram'].some(prefix => String(config.url || '').startsWith(prefix));
  if (!isAdminRequest && demoIsActiveInBrowser() && ['post', 'put', 'patch', 'delete'].includes(method) && guardedPath) {
    const error = new Error('Live mutation blocked while demo mode is active') as Error & { code: string };
    error.code = 'DEMO_LIVE_MUTATION_BLOCKED';
    throw error;
  }

  const token = isAdminRequest ? localStorage.getItem('admin_token') : localStorage.getItem('jwtToken');
  if (token && token !== 'null' && token.trim()) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (!isAdminRequest) {
    const comercioKey = localStorage.getItem('comercio_key') || localStorage.getItem('comercioKey');
    if (comercioKey) config.headers['x-comercio-key'] = comercioKey;
  }
  return config;
}

function rejectRequest(error: AxiosError): Promise<never> {
  return Promise.reject(error);
}

function passResponse(response: AxiosResponse): AxiosResponse {
  return response;
}

function handleAuthenticationFailure(error: AxiosError<{ detail?: { code?: string } }>): Promise<never> {
  const status = error.response?.status;
  const accessCode = error.response?.data?.detail?.code;
  const isAdminApp = typeof window !== 'undefined' && String(window.location.pathname || '').startsWith('/admin');

  if (status === 403 && accessCode === 'access_not_allowed' && !isAdminApp) {
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('comercioKey');
    localStorage.removeItem('comercio_key');
    localStorage.removeItem('timezone_iana');
    try { window.dispatchEvent(new Event('auth:logout')); } catch {}
    if (typeof window !== 'undefined' && window.location.pathname !== '/access-pending') {
      window.location.href = '/access-pending';
    }
    return Promise.reject(error);
  }

  if (status === 401 || status === 403) {
    try {
      if (isAdminApp) {
        localStorage.removeItem('admin_token');
        if (typeof window !== 'undefined' && !String(window.location.pathname || '').startsWith('/admin/login')) {
          window.location.href = '/admin/login';
        }
      } else {
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('comercioKey');
        localStorage.removeItem('comercio_key');
        localStorage.removeItem('timezone_iana');
        try { window.dispatchEvent(new Event('auth:logout')); } catch {}
        if (typeof window !== 'undefined' && !String(window.location.pathname || '').startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    } catch {}
  }
  return Promise.reject(error);
}

/** Install the shared auth policy through Axios's public API and return cleanup. */
export function installApiInterceptors(instance: AxiosInstance): () => void {
  const requestId = instance.interceptors.request.use(applyAuthentication, rejectRequest);
  const responseId = instance.interceptors.response.use(passResponse, handleAuthenticationFailure);
  let installed = true;

  return () => {
    if (!installed) return;
    instance.interceptors.request.eject(requestId);
    instance.interceptors.response.eject(responseId);
    installed = false;
  };
}

export const FASTAPI_BASE_URL: string =
  (import.meta as any)?.env?.VITE_FASTAPI_URL || 'http://localhost:8001/api/v1';

export const fastapi: AxiosInstance = axios.create({
  baseURL: FASTAPI_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

installApiInterceptors(api);
installApiInterceptors(fastapi);

export default api;
